import { useMemo, useState } from 'react';
import { hexToRgba, rgbaToCss, rgbaToHex } from '../../core/color';
import {
  COLOR_PALETTES,
  DEFAULT_PALETTE_ID,
  paletteById,
} from '../../core/colorPalettes';
import type { RGBA } from '../../core/types';
import { useAppStore } from '../../state/store';
import { NumberInput, Select } from '../components/controls';
import { Panel } from './Panel';

const PALETTE_OPTIONS = COLOR_PALETTES.map((palette) => ({
  value: palette.id,
  label: palette.label,
}));

export function ColorPanel() {
  const foreground = useAppStore((state) => state.foreground);
  const background = useAppStore((state) => state.background);
  const setForeground = useAppStore((state) => state.setForeground);
  const setBackground = useAppStore((state) => state.setBackground);
  const swapColors = useAppStore((state) => state.swapColors);
  const [paletteId, setPaletteId] = useState(DEFAULT_PALETTE_ID);
  const palette = useMemo(() => paletteById(paletteId), [paletteId]);

  const setChannel = (channel: keyof RGBA, value: number) => {
    setForeground({ ...foreground, [channel]: value / 255 });
  };

  return (
    <Panel id="color" title="Color">
      <div className="panel__body">
        <div className="color-panel">
          <div className="color-panel__swatches">
            <label
              className="toolbar__swatch"
              style={{ position: 'relative', width: 44, height: 26, background: rgbaToCss(foreground) }}
              title="Foreground color"
            >
              <input
                type="color"
                style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
                value={rgbaToHex(foreground)}
                onChange={(event) => setForeground(hexToRgba(event.target.value))}
              />
            </label>
            <label
              className="toolbar__swatch"
              style={{ position: 'relative', width: 44, height: 26, background: rgbaToCss(background) }}
              title="Background color"
            >
              <input
                type="color"
                style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
                value={rgbaToHex(background)}
                onChange={(event) => setBackground(hexToRgba(event.target.value))}
              />
            </label>
            <button type="button" className="button" onClick={swapColors} title="Swap colors (X)">
              ⇄
            </button>
          </div>

          <div className="color-panel__fields">
            <div className="field">
              <span className="field__label" style={{ width: 12 }}>
                R
              </span>
              <NumberInput
                value={Math.round(foreground.r * 255)}
                min={0}
                max={255}
                onChange={(value) => setChannel('r', value)}
              />
            </div>
            <div className="field">
              <span className="field__label" style={{ width: 12 }}>
                G
              </span>
              <NumberInput
                value={Math.round(foreground.g * 255)}
                min={0}
                max={255}
                onChange={(value) => setChannel('g', value)}
              />
            </div>
            <div className="field">
              <span className="field__label" style={{ width: 12 }}>
                B
              </span>
              <NumberInput
                value={Math.round(foreground.b * 255)}
                min={0}
                max={255}
                onChange={(value) => setChannel('b', value)}
              />
            </div>
            <input
              className="text-input"
              value={rgbaToHex(foreground)}
              onChange={(event) => setForeground(hexToRgba(event.target.value))}
              spellCheck={false}
            />
          </div>
        </div>

        <div className="color-panel__palette">
          <Select
            value={paletteId}
            options={PALETTE_OPTIONS}
            title="Swatch palette"
            onChange={setPaletteId}
          />
          <div className="swatch-grid">
            {palette.colors.map((hex, index) => (
              <button
                key={`${palette.id}-${index}-${hex}`}
                type="button"
                className="swatch-grid__item"
                style={{ background: hex }}
                title={`${hex} — click foreground, Alt+click background`}
                onClick={(event) => {
                  const color = hexToRgba(hex);
                  if (event.altKey) setBackground(color);
                  else setForeground(color);
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </Panel>
  );
}
